import type {GridComparatorFn, GridSortCellParams} from '@mui/x-data-grid';
import type {TFunction} from 'i18next';
import {getStatusLabel} from '@/components/common/forestMassStatusLabels';
import type {STATUS} from '@/domain/entities/ForestMassStatus';

//TYPES
export type Organization = 'finsa' | 'asefor' | 'fortop';

type StatusRow = {
  [org in Organization as `${org}Status`]: STATUS;
} & {
  [org in Organization as `${org}StatusChangeDate`]: {valueOf(): number};
};

/**
 * Builds a comparator that sorts rows alphabetically by the translated status
 * label of the given organization (i18n dependent), falling back to the
 * status change date when both statuses render the same label (RF2).
 */
export const createStatusSortComparator =
  (org: Organization, t: TFunction, lang: string): GridComparatorFn =>
  (v1, v2, cellParams1, cellParams2) => {
    const labelResult = getStatusLabel(v1, t).localeCompare(getStatusLabel(v2, t), lang);
    if (labelResult !== 0) {
      return labelResult;
    }

    const row1 = getRow<StatusRow>(cellParams1);
    const row2 = getRow<StatusRow>(cellParams2);
    const date1 = row1?.[`${org}StatusChangeDate`]?.valueOf() ?? 0;
    const date2 = row2?.[`${org}StatusChangeDate`]?.valueOf() ?? 0;
    return date1 - date2;
  };

const getRow = <R>(cellParams: GridSortCellParams): R | undefined => cellParams.api?.getRow(cellParams.id);
